import {
  getDietaryIngredients,
  useCustomerDietaryRestrictions,
} from '@hungryroot/dietary-restrictions';
import { useCustomerPeriodicities } from '@hungryroot/periodicities';
import {
  type DietaryRestrictions,
  type CartPairingIngredient,
  type PaginatedPeriodicityListList,
  usePairing,
} from '@hungryroot/rest';
import { type IngredientViewModel } from '../components/Ingredient/Ingredient';
import { containsNeverItem } from './useIngredientContainsNeverPeriodicity';
export type IngredientDetails = Record<number, IngredientViewModel>;

export function getIngredientDetails({
  ingredients,
  dietaryRestrictions,
  periodicities,
}: {
  ingredients: CartPairingIngredient[];
  dietaryRestrictions: DietaryRestrictions | undefined;
  periodicities: PaginatedPeriodicityListList | undefined;
}): IngredientDetails {
  if (!ingredients?.length || !dietaryRestrictions || !periodicities) {
    return {};
  }

  return (
    ingredients?.reduce((acc, ingredient) => {
      return {
        ...acc,
        [ingredient.id]: {
          ...ingredient,
          dietaryIngredients: getDietaryIngredients(
            ingredient.tags,
            dietaryRestrictions.dietary_restrictions || []
          ),
          containsNeverItem: containsNeverItem(
            [ingredient],
            periodicities.results || []
          ),
        },
      };
    }, {}) || {}
  );
}

export const useIngredientDetails = (
  id: number,
  options?: {
    enabled: boolean;
    outOfStockOptions?: {
      inventory_date: string;
      origin: number;
    };
  }
) => {
  const { data: dietaryRestrictionsData } = useCustomerDietaryRestrictions();
  const { data: periodicityData } = useCustomerPeriodicities();

  const { data } = usePairing(
    {
      id,
      ...(options?.outOfStockOptions ?? {}),
    },
    options
  );

  if (!data && (!periodicityData || !dietaryRestrictionsData)) {
    return {};
  }

  return getIngredientDetails({
    ingredients: data?.ingredients || [],
    dietaryRestrictions: dietaryRestrictionsData,
    periodicities: periodicityData,
  });
};
